import { memo, useMemo } from "react";
import { Handle } from "@xyflow/react";
import { Maximize2 } from "lucide-react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import { usePortalContainer } from "../context/PortalContainerContext";
import { buildUrl } from "../utils/url-builder";
import { getIcon } from "../utils/badges";
import {
  NODE_WIDTH_STYLE,
  ROTATED_LABEL_STYLE,
  TINY_FONT_STYLE,
  EMPTY_ARRAY,
} from "./shared-styles";
import { TruncatedResourceName } from "./TruncatedResourceName";

interface FlowData {
  id: string;
  version: string;
  name: string;
  summary?: string;
  owners?: string[];
  sidebar?: {
    badge?: string;
  };
  styles?: {
    backgroundColor?: string;
    borderColor?: string;
    icon?: string;
    node?: {
      color?: string;
      label?: string;
    };
  };
}

interface Data {
  label: string;
  bgColor: string;
  color: string;
  mode: "simple" | "full";
  flow: {
    data: FlowData;
  };
  showTarget?: boolean;
  showSource?: boolean;
}

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default memo(function FlowNode({
  data,
  sourcePosition,
  targetPosition,
}: any) {
  const { mode, flow } = data as Data;
  const canExpand =
    Array.isArray((data as any)?.expandedNodes) &&
    (data as any).expandedNodes.length > 0;

  const { id, version, owners = EMPTY_ARRAY, name, styles } = flow.data;
  const { node: { color = "teal", label } = {}, icon = "QueueListIcon" } =
    styles || {};

  const Icon = useMemo(() => getIcon(icon), [icon]);
  const portalContainer = usePortalContainer();
  const nodeLabel = label || flow?.data?.sidebar?.badge || "Flow";
  const fontSize = nodeLabel.length > 10 ? "7px" : "9px";

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>
        <div
          className="relative"
          style={{ isolation: "isolate", ...NODE_WIDTH_STYLE }}
        >
          {/* Stacked shadow layers behind the card signal a sub-flow the user can expand. */}
          {canExpand && (
            <>
              <div
                className={`absolute inset-0 rounded-md border border-${color}-400 bg-[rgb(var(--ec-card-bg))]`}
                style={{ transform: "translate(6px, 6px)", opacity: 0.5 }}
                aria-hidden
              />
              <div
                className={`absolute inset-0 rounded-md border border-${color}-400 bg-[rgb(var(--ec-card-bg))]`}
                style={{ transform: "translate(3px, 3px)", opacity: 0.7 }}
                aria-hidden
              />
            </>
          )}
          <div
            className={classNames(
              `relative rounded-md border flex justify-start bg-[rgb(var(--ec-card-bg))] text-[rgb(var(--ec-page-text))] border-${color}-400`,
            )}
            style={NODE_WIDTH_STYLE}
          >
            <div
              className={classNames(
                `bg-gradient-to-b from-${color}-500 to-${color}-700 relative flex flex-col items-center w-5 justify-between rounded-l-sm text-${color}-100`,
                `border-r-[1px] border-${color}-500`,
              )}
            >
              {Icon && <Icon className="w-4 h-4 opacity-90 text-white mt-1" />}
              {mode === "full" && (
                <span
                  className={`text-center text-[${fontSize}] text-white font-bold uppercase mb-4`}
                  style={ROTATED_LABEL_STYLE}
                >
                  {nodeLabel}
                </span>
              )}
            </div>
            <div className="p-1 flex-1 min-w-0">
              {targetPosition && (
                <Handle type="target" position={targetPosition} />
              )}
              {sourcePosition && (
                <Handle type="source" position={sourcePosition} />
              )}
              <div
                className={classNames(
                  mode === "full"
                    ? `border-b border-[rgb(var(--ec-page-border))]`
                    : "",
                )}
              >
                <TruncatedResourceName
                  as="div"
                  value={name}
                  tooltipBorderColor="#14b8a6"
                  className="text-xs font-bold truncate pt-0.5 pb-0.5"
                >
                  {name}
                </TruncatedResourceName>
                <div className="flex justify-between">
                  <span className="text-[10px] font-light block pt-0.5 pb-0.5 ">
                    v{version}
                  </span>
                  {mode === "simple" && (
                    <span className="text-[10px] text-[rgb(var(--ec-page-text-muted))] font-light block pt-0.5 pb-0.5 ">
                      {nodeLabel}
                    </span>
                  )}
                </div>
              </div>
              {mode === "full" && (
                <div className="divide-y divide-[rgb(var(--ec-page-border))] ">
                  <div className="leading-3 py-1">
                    <span className="text-[8px] font-light">
                      {flow.data.summary}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 py-1">
                    <span className="text-xs" style={TINY_FONT_STYLE}>
                      Owners: {owners.length}
                    </span>
                  </div>
                </div>
              )}
              {canExpand && (
                <div className="mt-1 flex items-center gap-1 text-[8px] text-[rgb(var(--ec-page-text-muted))]">
                  <Maximize2 className="w-2.5 h-2.5" />
                  Click to explore
                </div>
              )}
            </div>
          </div>
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal container={portalContainer}>
        <ContextMenu.Content className="min-w-[220px] bg-[rgb(var(--ec-card-bg))] rounded-md p-1 shadow-md border border-[rgb(var(--ec-page-border))]">
          <ContextMenu.Item
            asChild
            className="text-sm px-2 py-1.5 outline-none cursor-pointer hover:bg-orange-100 rounded-sm flex items-center"
          >
            <a href={buildUrl(`/docs/flows/${id}/${version}`)}>
              Read documentation
            </a>
          </ContextMenu.Item>
          <ContextMenu.Item
            asChild
            className="text-sm px-2 py-1.5 outline-none cursor-pointer hover:bg-orange-100 rounded-sm flex items-center"
          >
            <a href={buildUrl(`/visualiser/flows/${id}/${version}`)}>
              Focus node
            </a>
          </ContextMenu.Item>
          <ContextMenu.Separator className="h-[1px] bg-[rgb(var(--ec-page-border))] m-1" />
          <ContextMenu.Item asChild>
            <a
              href={buildUrl(`/docs/flows/${id}/${version}/changelog`)}
              className="text-sm px-2 py-1.5 outline-none cursor-pointer hover:bg-orange-100 rounded-sm flex items-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read changelog
            </a>
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
});
